import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Timeline } from "@xzdarcy/react-timeline-editor";
import Box from "@mui/material/Box";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import {
  applyVideoEditorEdl,
  commitVideoEditorClipUpdate,
  removeVideoEditorClip,
  removeVideoEditorClips,
  setVideoEditorPlayhead,
  setVideoEditorSelection,
  splitVideoEditorClipAtPlayhead,
} from "../../actions/videoEditor";
import {
  buildTimelineEffects,
  edlToTimelineRows,
  findClipAtPlayhead,
  getTrackTypeFromRow,
} from "./timelineAdapter";
import { getEditAreaNode, getTimelineScrollNode } from "./timelineInteractionUtils";
import {
  selectVideoEditorDuration,
  selectVideoEditorEdl,
  selectVideoEditorPlayhead,
  selectVideoEditorSelectedClipId,
  selectVideoEditorSelectedClipIds,
} from "./videoEditorSelectors";
import { getEdlDuration } from "./projectSchema";
import { buildSubtitleClipTextUpdates, edlHasSubtitleClips } from "./subtitleEdl";
import VideoEditorClipAction from "./VideoEditorClipAction";
import VideoEditorTimelineTrackAddButtons from "./VideoEditorTimelineTrackAddButtons";
import { appendTrackAddActionsToRows } from "./timelineTrackAddActions";
import VideoEditorTimelineDragOverlay from "./VideoEditorTimelineDragOverlay";
import useTimelineClipInteraction from "./useTimelineClipInteraction";
import useTimelineMarqueeSelection from "./useTimelineMarqueeSelection";
import {
  getClipSelectionMode,
  toSelectionModifiers,
  VIDEO_EDITOR_SELECTION_MODE,
} from "./clipSelection";
import {
  getTimelineRowScrollOffset,
  getTimelineRowsLayout,
  getTimelineShellContentHeight,
  getTimelineShellMinHeight,
} from "./timelineLayout";
import { useVideoEditorPlaybackClock } from "./videoEditorPlaybackClock";
import { useTranslationApp } from "../../lib/i18next";
import {
  MODAL_VIDEO_EDITOR_TIMELINE_ARIA,
  MODAL_VIDEO_EDITOR_TIMELINE_RESIZE,
} from "../../i18n/keysTranslations";
import {
  clampTimelineZoomLevel,
  getTimelineFitScaleWidth,
  getTimelineVisibleDuration,
  timeToTimelinePixels,
  timelinePixelsToTime,
  VIDEO_EDITOR_PLAYHEAD,
  VIDEO_EDITOR_TIMELINE_MAJOR_SCALE,
  VIDEO_EDITOR_TIMELINE_MAX_VISIBLE_ROWS,
  VIDEO_EDITOR_TIMELINE_MIN_PLAYER_HEIGHT,
  VIDEO_EDITOR_TIMELINE_RESIZE_HANDLE_HEIGHT,
  VIDEO_EDITOR_TIMELINE_ROW_HEIGHT,
  VIDEO_EDITOR_TIMELINE_SCALE,
  VIDEO_EDITOR_TIMELINE_SCALE_SPLIT_COUNT,
  VIDEO_EDITOR_TIMELINE_SCALE_WIDTH,
  VIDEO_EDITOR_TIMELINE_SECTION_PADDING_BOTTOM,
  VIDEO_EDITOR_TIMELINE_SECTION_PADDING_TOP,
  VIDEO_EDITOR_TIMELINE_START_LEFT,
  VIDEO_EDITOR_TIMELINE_SUBDIVISION_SECONDS,
  VIDEO_EDITOR_TIMELINE_ZOOM_STEP,
} from "./constants";
import "./videoEditorTimeline.css";

const TIMELINE_RESIZE_HANDLE_SELECTOR =
  "[data-video-editor-timeline-resize='true']";

const applyTimelineShellHeight = ({
  rootNode,
  areaNode,
  height,
  chromeHeight,
}) => {
  const editorHeight = Math.max(0, height - chromeHeight);
  if (rootNode) {
    rootNode.style.height = `${height}px`;
  }
  if (areaNode) {
    areaNode.style.height = `${editorHeight}px`;
  }
  rootNode
    ?.querySelector?.(TIMELINE_RESIZE_HANDLE_SELECTOR)
    ?.setAttribute?.("aria-valuenow", String(Math.round(height)));
};

const clearTimelineShellHeight = ({ rootNode, areaNode }) => {
  if (rootNode) {
    rootNode.style.height = "";
  }
  if (areaNode) {
    areaNode.style.height = "";
  }
};

const VideoEditorTimeline = ({
  onAddToTrack = () => {},
  onSplitClip = () => {},
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const edl = useSelectorApp(selectVideoEditorEdl);
  const playhead = useSelectorApp(selectVideoEditorPlayhead);
  const duration = useSelectorApp(selectVideoEditorDuration);
  const selectedClipId = useSelectorApp(selectVideoEditorSelectedClipId);
  const selectedClipIds = useSelectorApp(selectVideoEditorSelectedClipIds);
  const playbackClock = useVideoEditorPlaybackClock();
  const timelineRef = useRef(null);
  const timelineRootRef = useRef(null);
  const timelineAreaRef = useRef(null);
  const resizeDragRef = useRef(null);
  const hoverLineRef = useRef(null);
  const playheadLineRef = useRef(null);
  const playheadRafRef = useRef(null);
  const pendingPlayheadRef = useRef(null);
  const pendingZoomAnchorRef = useRef(null);
  const zoomLevelRef = useRef(1);
  const scaleWidthRef = useRef(VIDEO_EDITOR_TIMELINE_SCALE_WIDTH);
  const playheadRef = useRef(0);
  const [availableWidth, setAvailableWidth] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [timelineExtraHeight, setTimelineExtraHeight] = useState(0);
  const [isResizingTimeline, setIsResizingTimeline] = useState(false);
  const resizeRafRef = useRef(null);
  const isResizingTimelineRef = useRef(false);
  isResizingTimelineRef.current = isResizingTimeline;

  const layoutMinHeight = useMemo(
    () => getTimelineShellMinHeight({ edl }),
    [edl]
  );
  const layoutContentHeight = useMemo(
    () => getTimelineShellContentHeight({ edl }),
    [edl]
  );
  const layoutMinHeightRef = useRef(layoutMinHeight);
  layoutMinHeightRef.current = layoutMinHeight;
  const [availableColumnHeight, setAvailableColumnHeight] = useState(0);
  const layoutMaxHeight = useMemo(() => {
    const playerCapped =
      availableColumnHeight > 0
        ? availableColumnHeight - VIDEO_EDITOR_TIMELINE_MIN_PLAYER_HEIGHT
        : layoutContentHeight;
    return Math.max(
      layoutMinHeight,
      Math.min(layoutContentHeight, playerCapped)
    );
  }, [availableColumnHeight, layoutContentHeight, layoutMinHeight]);
  const layoutMaxHeightRef = useRef(layoutMaxHeight);
  layoutMaxHeightRef.current = layoutMaxHeight;
  const timelineRowCountRef = useRef(0);

  useEffect(() => {
    setTimelineExtraHeight(0);
  }, [layoutMinHeight]);

  const timelineChromeHeight =
    VIDEO_EDITOR_TIMELINE_SECTION_PADDING_TOP +
    VIDEO_EDITOR_TIMELINE_SECTION_PADDING_BOTTOM;
  const timelineChromeHeightRef = useRef(timelineChromeHeight);
  timelineChromeHeightRef.current = timelineChromeHeight;
  const timelineHeight =
    layoutMinHeight +
    Math.min(
      Math.max(0, timelineExtraHeight),
      Math.max(0, layoutMaxHeight - layoutMinHeight)
    );
  const editorHeight = Math.max(0, timelineHeight - timelineChromeHeight);

  const effects = useMemo(() => buildTimelineEffects(), []);

  const playbackDuration = duration || getEdlDuration({ edl }) || 0;
  const resolvedDuration = playbackDuration || 10;
  const visibleDuration = getTimelineVisibleDuration({
    durationSeconds: resolvedDuration,
  });
  const clampPlayhead = useCallback(
    (time) =>
      Math.min(
        playbackDuration,
        Math.max(0, Number(time) || 0)
      ),
    [playbackDuration]
  );

  const minScaleCount = useMemo(
    () => Math.max(1, Math.ceil(visibleDuration / VIDEO_EDITOR_TIMELINE_SCALE)),
    [visibleDuration]
  );

  const fittedScaleWidth = useMemo(() => {
    if (!availableWidth) {
      return VIDEO_EDITOR_TIMELINE_SCALE_WIDTH;
    }

    return getTimelineFitScaleWidth({
      availableWidth,
      durationSeconds: resolvedDuration,
    });
  }, [availableWidth, resolvedDuration]);

  const scaleWidth = fittedScaleWidth * zoomLevel;
  zoomLevelRef.current = zoomLevel;
  scaleWidthRef.current = scaleWidth;
  playheadRef.current = playhead;

  const onCommitEdl = useCallback(
    ({ edl: nextEdl }) => {
      dispatch(applyVideoEditorEdl({ edl: nextEdl }));
    },
    [dispatch]
  );

  const onSelectClip = useCallback(
    ({ clipId, event, preserveMulti = false }) => {
      const modifiers = toSelectionModifiers(event);
      dispatch(
        setVideoEditorSelection({
          clipId,
          event: modifiers,
          mode: getClipSelectionMode(modifiers),
          preserveMulti,
        })
      );
    },
    [dispatch]
  );

  const {
    displayEdl,
    dragState,
    draggingClipIds,
    ghostNodeRef,
    isDragging,
    onClipPointerDown,
    highlightedTrackId,
  } = useTimelineClipInteraction({
    edl,
    playhead,
    scaleWidth,
    timelineAreaRef,
    timelineRef,
    selectedClipId,
    selectedClipIds,
    onCommitEdl,
    onSelectClip,
  });

  const onSelectMarqueeClipIds = useCallback(
    ({ clipIds, additive }) => {
      dispatch(
        setVideoEditorSelection({
          clipIds,
          mode: additive
            ? VIDEO_EDITOR_SELECTION_MODE.ADD
            : VIDEO_EDITOR_SELECTION_MODE.REPLACE,
        })
      );
    },
    [dispatch]
  );

  const { marquee, onTimelinePointerDown, isMarqueeActive } =
    useTimelineMarqueeSelection({
      edl,
      scaleWidth,
      timelineAreaRef,
      enabled: Boolean(edl) && !isDragging,
      onSelectClipIds: onSelectMarqueeClipIds,
    });

  const localRows = useMemo(
    () => {
      const clipRows = edlToTimelineRows({
        edl: displayEdl,
        selectedClipIds,
        draggingClipIds,
      });

      return appendTrackAddActionsToRows({
        rows: clipRows,
        scaleWidth,
        showAddSubtitles: !edlHasSubtitleClips(displayEdl),
      });
    },
    [displayEdl, draggingClipIds, scaleWidth, selectedClipIds]
  );

  const getTimelineScrollLeft = useCallback(
    () => getTimelineScrollNode(timelineAreaRef.current)?.scrollLeft || 0,
    []
  );

  const syncPlayheadOverlay = useCallback(
    (time) => {
      if (!playheadLineRef.current) {
        return;
      }

      const nextX =
        timeToTimelinePixels({
          time,
          scaleWidth: scaleWidthRef.current,
          scale: VIDEO_EDITOR_TIMELINE_SCALE,
          startLeft: VIDEO_EDITOR_TIMELINE_START_LEFT,
        }) - getTimelineScrollLeft();
      playheadLineRef.current.style.left = `${nextX}px`;
    },
    [getTimelineScrollLeft]
  );

  const onGetScaleRender = useCallback((time) => {
    const rounded = Math.round(time);
    if (rounded % VIDEO_EDITOR_TIMELINE_SUBDIVISION_SECONDS !== 0) {
      return null;
    }

    const isMajorTick = rounded % VIDEO_EDITOR_TIMELINE_MAJOR_SCALE === 0;

    return (
      <span
        style={{
          fontSize: 10,
          fontWeight: isMajorTick ? 700 : 500,
          color: "#667387",
          lineHeight: 1,
        }}
      >
        {isMajorTick ? `${rounded}s` : ""}
      </span>
    );
  }, []);

  useEffect(() => {
    const node = timelineAreaRef.current;
    if (!node) return undefined;

    const onResize = () => {
      if (isResizingTimelineRef.current) return;
      setAvailableWidth(node.clientWidth);
    };

    onResize();
    const observer = new ResizeObserver(onResize);
    observer.observe(node);

    return () => observer.disconnect();
  }, [edl]);

  useEffect(() => {
    const column = timelineRootRef.current?.parentElement;
    if (!column) return undefined;

    const onResize = () => {
      setAvailableColumnHeight(column.clientHeight);
    };

    onResize();
    const observer = new ResizeObserver(onResize);
    observer.observe(column);

    return () => observer.disconnect();
  }, [edl]);

  useEffect(() => {
    const node = timelineAreaRef.current;
    if (!node) return undefined;

    const onWheel = (event) => {
      if (!event.ctrlKey && !event.metaKey) {
        return;
      }

      event.preventDefault();
      const editArea = getEditAreaNode(node);
      const scrollNode = getTimelineScrollNode(node);
      const editRect = (editArea || node).getBoundingClientRect();
      const scrollLeft = scrollNode?.scrollLeft || 0;
      const cursorX = event.clientX - editRect.left + scrollLeft;
      const anchorTime = timelinePixelsToTime({
        pixels: cursorX,
        scaleWidth: scaleWidthRef.current,
        scale: VIDEO_EDITOR_TIMELINE_SCALE,
        startLeft: VIDEO_EDITOR_TIMELINE_START_LEFT,
      });
      const factor =
        event.deltaY < 0
          ? VIDEO_EDITOR_TIMELINE_ZOOM_STEP
          : 1 / VIDEO_EDITOR_TIMELINE_ZOOM_STEP;
      const nextZoom = clampTimelineZoomLevel(zoomLevelRef.current * factor);
      if (nextZoom === zoomLevelRef.current) {
        return;
      }

      pendingZoomAnchorRef.current = {
        time: anchorTime,
        viewX: event.clientX - editRect.left,
      };
      setZoomLevel(nextZoom);
    };

    node.addEventListener("wheel", onWheel, { passive: false });
    return () => node.removeEventListener("wheel", onWheel);
  }, [edl]);

  useEffect(() => {
    const scrollNode = getTimelineScrollNode(timelineAreaRef.current);
    if (!scrollNode) return undefined;

    const onScroll = () => {
      const timeArea = timelineAreaRef.current?.querySelector?.(
        ".timeline-editor-time-area"
      );
      if (timeArea && timeArea.scrollLeft !== scrollNode.scrollLeft) {
        timeArea.scrollLeft = scrollNode.scrollLeft;
      }
      syncPlayheadOverlay(playheadRef.current);
    };

    scrollNode.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => scrollNode.removeEventListener("scroll", onScroll);
  }, [edl, scaleWidth, syncPlayheadOverlay]);

  useEffect(() => {
    const anchor = pendingZoomAnchorRef.current;
    if (!anchor || !timelineRef.current) {
      return;
    }

    pendingZoomAnchorRef.current = null;
    const contentX = timeToTimelinePixels({
      time: anchor.time,
      scaleWidth,
      scale: VIDEO_EDITOR_TIMELINE_SCALE,
      startLeft: VIDEO_EDITOR_TIMELINE_START_LEFT,
    });
    timelineRef.current.setScrollLeft?.(Math.max(0, contentX - anchor.viewX));
    syncPlayheadOverlay(playheadRef.current);
  }, [scaleWidth, syncPlayheadOverlay]);

  useEffect(() => {
    if (!timelineRef.current || !edl) return;
    setZoomLevel(1);
    timelineRef.current.setScrollLeft(0);
    timelineRef.current.setScrollTop?.(0);
    // Reset scroll only when opening a different source video.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sourceAssetID identifies the project
  }, [edl?.sourceAssetID]);

  useEffect(() => {
    if (!timelineRef.current || !edl) {
      return;
    }
    const { rows } = getTimelineRowsLayout({ edl });
    const nextCount = rows.length;
    const previousCount = timelineRowCountRef.current;
    timelineRowCountRef.current = nextCount;
    if (
      nextCount <= VIDEO_EDITOR_TIMELINE_MAX_VISIBLE_ROWS &&
      previousCount > VIDEO_EDITOR_TIMELINE_MAX_VISIBLE_ROWS
    ) {
      timelineRef.current.setScrollTop?.(0);
    }
  }, [edl]);

  useEffect(() => {
    if (!timelineRef.current || isDragging) return;
    const clockTime = playbackClock.get();
    if (
      clockTime != null &&
      Math.abs(clockTime - playhead) > 0.05
    ) {
      return;
    }
    timelineRef.current.setTime(playhead);
  }, [isDragging, playhead, playbackClock]);

  useLayoutEffect(() => {
    const syncFromClock = (time) => {
      playheadRef.current = time;
      syncPlayheadOverlay(time);
    };
    syncFromClock(playbackClock.get() ?? playhead);
    return playbackClock.subscribe(syncFromClock);
  }, [playhead, playbackClock, syncPlayheadOverlay]);

  const flushPlayhead = useCallback(() => {
    if (pendingPlayheadRef.current == null) {
      return;
    }
    dispatch(setVideoEditorPlayhead({ playhead: pendingPlayheadRef.current }));
    pendingPlayheadRef.current = null;
  }, [dispatch]);

  const onCursorDrag = useCallback(
    (time) => {
      const nextTime = clampPlayhead(time);
      playbackClock.set(nextTime);
      pendingPlayheadRef.current = nextTime;
      if (playheadLineRef.current) {
        const nextX =
          timeToTimelinePixels({
            time: nextTime,
            scaleWidth,
            scale: VIDEO_EDITOR_TIMELINE_SCALE,
            startLeft: VIDEO_EDITOR_TIMELINE_START_LEFT,
          }) - getTimelineScrollLeft();
        playheadLineRef.current.style.left = `${nextX}px`;
      }
      if (hoverLineRef.current) {
        hoverLineRef.current.style.opacity = "0";
      }
      if (!playheadRafRef.current) {
        playheadRafRef.current = window.requestAnimationFrame(() => {
          playheadRafRef.current = null;
          flushPlayhead();
        });
      }
    },
    [
      clampPlayhead,
      flushPlayhead,
      getTimelineScrollLeft,
      playbackClock,
      scaleWidth,
    ]
  );

  const onCursorDragEnd = useCallback(
    (time) => {
      if (playheadRafRef.current) {
        window.cancelAnimationFrame(playheadRafRef.current);
        playheadRafRef.current = null;
      }
      const nextTime = clampPlayhead(time);
      playbackClock.set(nextTime);
      dispatch(setVideoEditorPlayhead({ playhead: nextTime }));
      timelineRef.current?.setTime?.(nextTime);
    },
    [clampPlayhead, dispatch, playbackClock]
  );

  const onSplitAtPlayhead = useCallback(
    (_event, { row }) => {
      if (!edl) return;
      const time = playbackClock.get() || playhead;
      dispatch(setVideoEditorPlayhead({ playhead: time }));
      const clip = findClipAtPlayhead({ edl, playhead: time });
      if (!clip) return;

      const rowType = getTrackTypeFromRow({ row });
      if (clip.type !== rowType) return;

      dispatch(splitVideoEditorClipAtPlayhead({ clipId: clip.id }));
      onSplitClip();
    },
    [dispatch, edl, onSplitClip, playhead, playbackClock]
  );

  const onSeekToTime = useCallback(
    ({ time }) => {
      const nextTime = clampPlayhead(time);
      playbackClock.set(nextTime);
      dispatch(setVideoEditorPlayhead({ playhead: nextTime }));
      timelineRef.current?.setTime?.(nextTime);
    },
    [clampPlayhead, dispatch, playbackClock]
  );

  const onHoverTimeline = useCallback(
    (event) => {
      const line = hoverLineRef.current;
      const node = timelineAreaRef.current;
      if (!line || !node || isDragging || isResizingTimeline) {
        if (line) line.style.opacity = "0";
        return;
      }

      const target = event.target;
      if (
        target?.closest?.("[data-video-editor-clip-menu='true']") ||
        target?.closest?.("[data-video-editor-track-add='true']") ||
        target?.closest?.("[data-video-editor-timeline-resize='true']")
      ) {
        line.style.opacity = "0";
        return;
      }

      line.style.left = `${event.clientX - node.getBoundingClientRect().left}px`;
      line.style.opacity = "1";
    },
    [isDragging, isResizingTimeline]
  );

  const onLeaveTimeline = useCallback(() => {
    if (hoverLineRef.current) {
      hoverLineRef.current.style.opacity = "0";
    }
  }, []);

  const onResizePointerDown = useCallback(
    (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();

      resizeDragRef.current = {
        pointerId: event.pointerId,
        startY: event.clientY,
        startHeight: timelineHeight,
        pendingHeight: timelineHeight,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
      setIsResizingTimeline(true);
    },
    [timelineHeight]
  );

  const onResizePointerMove = useCallback((event) => {
    const drag = resizeDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const minHeight = layoutMinHeightRef.current;
    const maxHeight = layoutMaxHeightRef.current;
    const nextHeight = drag.startHeight + (drag.startY - event.clientY);
    drag.pendingHeight = Math.min(
      maxHeight,
      Math.max(minHeight, nextHeight)
    );

    if (resizeRafRef.current) return;
    resizeRafRef.current = window.requestAnimationFrame(() => {
      resizeRafRef.current = null;
      const current = resizeDragRef.current;
      if (!current) return;
      applyTimelineShellHeight({
        rootNode: timelineRootRef.current,
        areaNode: timelineAreaRef.current,
        height: current.pendingHeight,
        chromeHeight: timelineChromeHeightRef.current,
      });
    });
  }, []);

  const onResizePointerUp = useCallback((event) => {
    const drag = resizeDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    if (resizeRafRef.current) {
      window.cancelAnimationFrame(resizeRafRef.current);
      resizeRafRef.current = null;
    }

    const minHeight = layoutMinHeightRef.current;
    const nextHeight = drag.pendingHeight;
    applyTimelineShellHeight({
      rootNode: timelineRootRef.current,
      areaNode: timelineAreaRef.current,
      height: nextHeight,
      chromeHeight: timelineChromeHeightRef.current,
    });
    resizeDragRef.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setTimelineExtraHeight(nextHeight - minHeight);
    setIsResizingTimeline(false);
  }, []);

  const onResizeKeyDown = useCallback(
    (event) => {
      if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
      event.preventDefault();

      const minHeight = layoutMinHeightRef.current;
      const maxHeight = layoutMaxHeightRef.current;
      const delta = event.key === "ArrowUp" ? 24 : -24;

      setTimelineExtraHeight((currentExtra) => {
        const current = minHeight + currentExtra;
        const next = Math.min(maxHeight, Math.max(minHeight, current + delta));
        return next - minHeight;
      });
    },
    []
  );

  useEffect(() => {
    if (!isResizingTimeline) return undefined;

    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "ns-resize";
    document.body.style.userSelect = "none";

    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };
  }, [isResizingTimeline]);

  useLayoutEffect(() => {
    if (isResizingTimeline) return;
    clearTimelineShellHeight({
      rootNode: timelineRootRef.current,
      areaNode: timelineAreaRef.current,
    });
    const areaWidth = timelineAreaRef.current?.clientWidth;
    if (typeof areaWidth === "number" && areaWidth > 0) {
      setAvailableWidth((current) =>
        current === areaWidth ? current : areaWidth
      );
    }
  }, [isResizingTimeline, timelineHeight]);

  useEffect(
    () => () => {
      if (resizeRafRef.current) {
        window.cancelAnimationFrame(resizeRafRef.current);
      }
    },
    []
  );

  const onDeleteClip = useCallback(
    ({ clipId }) => {
      dispatch(removeVideoEditorClip({ clipId }));
    },
    [dispatch]
  );

  const onSplitClipFromMenu = useCallback(
    ({ clipId }) => {
      dispatch(splitVideoEditorClipAtPlayhead({ clipId }));
      onSplitClip();
    },
    [dispatch, onSplitClip]
  );

  useEffect(() => {
    const onKeyDown = (event) => {
      if (isDragging) return;
      const target = event.target;
      if (target?.closest?.("input, textarea, [contenteditable='true']")) {
        return;
      }
      if (!selectedClipIds.length) return;

      const isSplitHotkey =
        (event.key === "s" || event.key === "S") &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey;
      const isDeleteHotkey =
        event.key === "Delete" || event.key === "Backspace";

      if (isSplitHotkey) {
        event.preventDefault();
        if (selectedClipId) {
          dispatch(splitVideoEditorClipAtPlayhead({ clipId: selectedClipId }));
          onSplitClip();
        }
        return;
      }

      if (!isDeleteHotkey) return;
      event.preventDefault();
      dispatch(removeVideoEditorClips({ clipIds: selectedClipIds }));
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [dispatch, isDragging, onSplitClip, selectedClipId, selectedClipIds]);

  const onGetActionRender = useCallback(
    (action) => {
      if (action?.data?.isTrackAdd) {
        return (
          <VideoEditorTimelineTrackAddButtons
            action={action}
            onAddToTrack={onAddToTrack}
          />
        );
      }

      return (
        <VideoEditorClipAction
          action={action}
          isSelected={selectedClipIds.includes(action?.id)}
          isDragging={draggingClipIds.includes(action?.id)}
          canSplit
          frozenPreview={Boolean(draggingClipIds.length)}
          onSplitClip={onSplitClipFromMenu}
          onDeleteClip={onDeleteClip}
          onPointerDownClip={onClipPointerDown}
          onSelectClip={onSelectClip}
          onCommitClipText={({ clipId, text }) => {
            const clip = edl?.clips?.byId?.[clipId];
            dispatch(
              commitVideoEditorClipUpdate({
                clipId,
                updates: buildSubtitleClipTextUpdates({ clip, text }),
              })
            );
          }}
        />
      );
    },
    [
      draggingClipIds,
      onAddToTrack,
      onClipPointerDown,
      onDeleteClip,
      onSelectClip,
      onSplitClipFromMenu,
      selectedClipIds,
      dispatch,
      edl,
    ]
  );

  const overlayScrollTop = getTimelineRowScrollOffset(timelineAreaRef.current);

  if (!edl) {
    return null;
  }

  return (
    <Box
      ref={timelineRootRef}
      className={`video-editor-timeline${isDragging ? " is-dragging" : ""}${
        isResizingTimeline ? " is-resizing" : ""
      }${isMarqueeActive ? " is-marquee" : ""}`}
      role="region"
      aria-label={t(MODAL_VIDEO_EDITOR_TIMELINE_ARIA)}
      sx={{
        position: "relative",
        width: "100%",
        height: timelineHeight,
        minHeight: layoutMinHeight,
        maxHeight: layoutMaxHeight,
        pt: `${VIDEO_EDITOR_TIMELINE_SECTION_PADDING_TOP}px`,
        pb: `${VIDEO_EDITOR_TIMELINE_SECTION_PADDING_BOTTOM}px`,
        borderTop: "1px solid #d8dee8",
        overflow: "hidden",
        display: "flex",
        bgcolor: "#f7f8fb",
        flexShrink: 0,
      }}
    >
      <Box
        data-video-editor-timeline-resize="true"
        role="separator"
        aria-orientation="horizontal"
        aria-label={t(MODAL_VIDEO_EDITOR_TIMELINE_RESIZE)}
        aria-valuemin={Math.round(layoutMinHeight)}
        aria-valuemax={Math.round(layoutMaxHeight)}
        aria-valuenow={Math.round(timelineHeight)}
        tabIndex={0}
        onPointerDown={onResizePointerDown}
        onPointerMove={onResizePointerMove}
        onPointerUp={onResizePointerUp}
        onPointerCancel={onResizePointerUp}
        onKeyDown={onResizeKeyDown}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: VIDEO_EDITOR_TIMELINE_RESIZE_HANDLE_HEIGHT,
          transform: "translateY(-50%)",
          zIndex: 6,
          cursor: "ns-resize",
          touchAction: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "&:hover .video-editor-timeline-resize-grip, &:focus-visible .video-editor-timeline-resize-grip":
            {
              backgroundColor: VIDEO_EDITOR_PLAYHEAD,
            },
        }}
      >
        <Box
          className="video-editor-timeline-resize-grip"
          sx={{
            width: 36,
            height: 4,
            borderRadius: 2,
            backgroundColor: isResizingTimeline ? VIDEO_EDITOR_PLAYHEAD : "#c5ceda",
            pointerEvents: "none",
          }}
        />
      </Box>
      <Box
        ref={timelineAreaRef}
        onMouseMove={onHoverTimeline}
        onMouseLeave={onLeaveTimeline}
        onPointerDown={onTimelinePointerDown}
        sx={{
          flex: 1,
          minWidth: 0,
          height: editorHeight,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Timeline
          ref={timelineRef}
          editorData={localRows}
          effects={effects}
          scale={VIDEO_EDITOR_TIMELINE_SCALE}
          scaleSplitCount={VIDEO_EDITOR_TIMELINE_SCALE_SPLIT_COUNT}
          scaleWidth={scaleWidth}
          minScaleCount={minScaleCount}
          maxScaleCount={minScaleCount + 24}
          startLeft={VIDEO_EDITOR_TIMELINE_START_LEFT}
          rowHeight={VIDEO_EDITOR_TIMELINE_ROW_HEIGHT}
          gridSnap={false}
          dragLine={false}
          autoScroll
          getScaleRender={onGetScaleRender}
          onCursorDrag={onCursorDrag}
          onCursorDragEnd={onCursorDragEnd}
          onClickTimeArea={(time) => {
            onSeekToTime({ time });
            return false;
          }}
          onClickRow={(_event, { time }) => {
            if (isMarqueeActive || isDragging) {
              return;
            }
            onSeekToTime({ time });
          }}
          onClickActionOnly={(_event, { time }) => {
            onSeekToTime({ time });
          }}
          onDoubleClickRow={onSplitAtPlayhead}
          getActionRender={onGetActionRender}
          style={{
            width: "100%",
            height: editorHeight,
          }}
        />
        <VideoEditorTimelineDragOverlay
          edl={displayEdl}
          dragState={dragState}
          ghostNodeRef={ghostNodeRef}
          highlightedTrackId={highlightedTrackId}
          scrollTop={overlayScrollTop}
          marquee={
            marquee
              ? {
                  ...marquee,
                  x: marquee.x - getTimelineScrollLeft(),
                  y: marquee.y,
                }
              : null
          }
        />
        <div ref={hoverLineRef} className="video-editor-timeline-hover-cursor" />
        <div
          ref={playheadLineRef}
          className="video-editor-timeline-playhead"
        />
      </Box>
    </Box>
  );
};

export default VideoEditorTimeline;
